import { create } from "zustand";
import { persist } from "zustand/middleware";
import { schoolApi } from "../api/school";
import { majorApi } from "../api/major";
import { AdministrationApi } from "../api/administration";
import { officesApi } from "../api/offices";
import { centersApi } from "../api/centers";
import { membersApi } from "../api/members";
import { presidentMsgApi } from "../api/presidentMsg";
import { faqsApi } from "../api/faq";
import { programsApi } from "../api/programs";
import { vacanciesApi } from "../api/vacancies";
import { newslettersApi } from "../api/newsletters";
import { scholarshipsApi } from "../api/scholarships";
import { tendersApi } from "../api/tenders";
import { newsApi } from "../api/news";
import asyncStorage from "./asyncStorage";

// const EXPIRY_TIME = 10 * 60 * 1000; // 10 minutes
const EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24 hours

export type ProjectState = {
  schools: { [key: string]: any };
  majors: { [key: string]: any };
  administration: { [key: string]: any };
  offices: { [key: string]: any };
  centers: { [key: string]: any };
  members: { [key: string]: any };
  presidentMessage: { [key: string]: any };
  faqs: { [key: string]: any };
  programs: { [key: string]: any };
  vacancies: { [key: string]: any };
  newsletters: { [key: string]: any };
  program: { [key: string]: any };
  scholarships: { [key: string]: any };
  activeRfi: { [key: string]: any };
  tenders: { [key: string]: any };
  news: { [key: string]: any };
  profile: { [key: string]: any };
  centerPrograms: { [key: string]: any };
  traineeCv: { [key: string]: any };
  facilities: { [key: string]: any };
  newslettersById: { [key: string]: any };
};

export type ProjectActions = {
  rehydrateSchool: (id: string) => Promise<void>;
  rehydrateMajor: (id: string) => Promise<void>;
  rehydrateAdministration: (id: string) => Promise<void>;
  rehydrateOffice: (id: string) => Promise<void>;
  rehydrateCenters: (id: string) => Promise<void>;
  rehydrateMembers: (id: string) => Promise<void>;
  rehydratePresidentMessage: () => Promise<void>;
  rehydrateFaqs: (id: string) => Promise<void>;
  rehydratePrograms: () => Promise<void>;
  rehydrateVacancies: () => Promise<void>;
  rehydrateNewsletters: () => Promise<void>;
  rehydrateProgramById: (id: string) => Promise<void>;
  rehydrateScholarships: () => Promise<void>;
  rehydrateRfi: () => Promise<void>;
  rehydrateTenders: () => Promise<void>;
  rehydrateNewsById: (id: string) => Promise<void>;
  rehydrateProfile: (id: string) => Promise<void>;
  rehydrateCenterPrograms: (id: string) => Promise<void>;
  rehydrateTraineeCv: (id: string) => Promise<void>;
  rehydrateFacility: (id: string) => Promise<void>;
  rehydrateNewsLetterById: (id: string) => Promise<void>;
  reset: () => void;
};

export type ProjectStore = ProjectState & ProjectActions;

export const initialState: ProjectState = {
  schools: {},
  majors: {},
  administration: {},
  offices: {},
  centers: {},
  members: {},
  presidentMessage: {},
  faqs: {},
  programs: {},
  vacancies: {},
  newsletters: {},
  program: {},
  scholarships: {},
  activeRfi: {},
  tenders: {},
  news: {},
  profile: {},
  centerPrograms: {},
  traineeCv: {},
  facilities: {},
  newslettersById: {},
};

export const isExpired = (timestamp: number) => {
  return Date.now() - timestamp > EXPIRY_TIME;
};

export const createProjectStore = () =>
  create<ProjectStore>()(
    persist(
      (set) => ({
        ...initialState,
        rehydrateSchool: async (id: string) => {
          try {
            const school = await schoolApi.getSchool(id);
            set((state) => ({
              schools: {
                ...state.schools,
                [id]: { ...school, timestamp: Date.now() },
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydrateMajor: async (id: string) => {
          try {
            const major = await majorApi.getMajor(id);
            set((state) => ({
              majors: {
                ...state.majors,
                [id]: { ...major, timestamp: Date.now() },
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydrateAdministration: async (id: string) => {
          try {
            const administration = await AdministrationApi.getAdministration(
              id
            );
            set((state) => ({
              administration: {
                ...state.administration,
                [id]: { ...administration, timestamp: Date.now() },
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydrateOffice: async (id: string) => {
          try {
            const offices = await officesApi.getOffice(id);
            set((state) => ({
              offices: {
                ...state.offices,
                [id]: { ...offices, timestamp: Date.now() },
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydrateCenters: async (id: string) => {
          try {
            const centers = await centersApi.getCenter(id);
            set((state) => ({
              centers: {
                ...state.centers,
                [id]: { ...centers, timestamp: Date.now() },
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydrateMembers: async (id: string) => {
          try {
            const members = await membersApi.getMembersById(id);
            set((state) => ({
              members: {
                ...state.members,
                [id]: { ...members, timestamp: Date.now() },
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydrateFaqs: async (id: string) => {
          try {
            const faqs = await faqsApi.getFaqsById(id);
            set((state) => ({
              faqs: {
                ...state.faqs,
                [id]: { faqs, timestamp: Date.now() },
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydratePresidentMessage: async () => {
          try {
            const presidentMessage = await presidentMsgApi.getPresidentMsg();
            set(() => ({
              presidentMessage: {
                ...presidentMessage,
                timestamp: Date.now(),
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydratePrograms: async () => {
          try {
            const programs = await programsApi.getPrograms();
            set(() => ({
              programs: {
                data: programs,
                timestamp: Date.now(),
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydrateVacancies: async () => {
          try {
            const vacancies = await vacanciesApi.getVacancies();
            set(() => ({
              vacancies: {
                data: vacancies,
                timestamp: Date.now(),
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydrateNewsletters: async () => {
          try {
            const newsletters = await newslettersApi.getNewsletters();
            set(() => ({
              newsletters: {
                data: newsletters,
                timestamp: Date.now(),
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydrateProgramById: async (id: string) => {
          try {
            const program = await programsApi.getProgramById(id);
            set((state) => ({
              program: {
                ...state.program,
                [id]: { ...program, timestamp: Date.now() },
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydrateScholarships: async () => {
          try {
            const scholarships = await scholarshipsApi.getScholarships();
            set(() => ({
              scholarships: {
                data: scholarships,
                timestamp: Date.now(),
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydrateRfi: async () => {
          try {
            const activeRfi = await tendersApi.getActiveRfi();
            set(() => ({
              activeRfi: {
                data: activeRfi,
                timestamp: Date.now(),
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydrateTenders: async () => {
          try {
            const tenders = await tendersApi.getTenders();
            set(() => ({
              tenders: {
                data: tenders,
                timestamp: Date.now(),
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydrateNewsById: async (id: string) => {
          try {
            const news = await newsApi.getNewsById(id);
            set((state) => ({
              news: {
                ...state.news,
                [id]: { ...news, timestamp: Date.now() },
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydrateProfile: async (id: string) => {
          try {
            const profile = await membersApi.getProfile(id);
            set((state) => ({
              profile: {
                ...state.profile,
                [id]: { ...profile, timestamp: Date.now() },
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydrateCenterPrograms: async (id: string) => {
          try {
            const centerPrograms = await programsApi.getCenterPrograms(id);
            set((state) => ({
              centerPrograms: {
                ...state.centerPrograms,
                [id]: { ...centerPrograms, timestamp: Date.now() },
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydrateTraineeCv: async (id: string) => {
          try {
            const traineeCv = await programsApi.getTraineeCV(id);
            set((state) => ({
              traineeCv: {
                ...state.traineeCv,
                [id]: { data: traineeCv, timestamp: Date.now() },
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydrateFacility: async (id: string) => {
          try {
            const facility = await schoolApi.getSchoolFacilityByUrl(id);
            set((state) => ({
              facilities: {
                ...state.facilities,
                [id]: { ...facility, timestamp: Date.now() },
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        rehydrateNewsLetterById: async (id: string) => {
          try {
            const newsletter = await newslettersApi.getNewsletterByUrl(id);
            set((state) => ({
              newslettersById: {
                ...state.newslettersById,
                [id]: { ...newsletter, timestamp: Date.now() },
              },
            }));
          } catch (e) {
            throw e;
          }
        },
        reset: () => {
          set(initialState);
        },
      }),
      {
        name: "project-storage",
        storage: asyncStorage,
        partialize: (state) => ({
          schools: state.schools,
          majors: state.majors,
          administration: state.administration,
          offices: state.offices,
          centers: state.centers,
          members: state.members,
          presidentMessage: state.presidentMessage,
          faqs: state.faqs,
          programs: state.programs,
          vacancies: state.vacancies,
          newsletters: state.newsletters,
          program: state.program,
          scholarships: state.scholarships,
          activeRfi: state.activeRfi,
          tenders: state.tenders,
          news: state.news,
          profile: state.profile,
          centerPrograms: state.centerPrograms,
          traineeCv: state.traineeCv,
          facilities: state.facilities,
          newslettersById: state.newslettersById,
        }),
      }
    )
  );
